import { Component, OnInit, inject, model, signal } from '@angular/core';
import { UserOperation } from '../../../../../../core/enums/user-operation';
import { UserModel } from '../../../../../../core/models/user/user.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ShiveDialogService } from '../../../../../../core/services/controls/shive-dialog.service';
import { FormControlNameRecord } from '../../../../../../shared/data-types/form-types';
import Autobind from '../../../../../../shared/typescript-decorators/autobind.decorator';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { ProjectMemberGridService } from '../../manage-project/project-member-grid/project-member-grid.service';
import { ProjectMemberGridRow } from '../../../../shared/project-detail-types';
import { DateTimeHelper } from '../../../../../../features/calendar/util/date-time-helper';
import { ExpenditureRateEntry } from '../../../../../../core/models/project/project-expenditure-entry.model';

@Component({
  selector: 'app-project-member-dialog',
  templateUrl: './project-member-dialog.component.html',
  standalone: false,
})
export class ProjectMemberDialogComponent implements OnInit {
  // Inputs
  readonly userOperation = model.required<UserOperation>();
  readonly selectedIndex = model.required();
  readonly memberGridRow = model<ProjectMemberGridRow>(null);
  readonly membersInGrid = model<UserModel[]>([]);
  readonly rateAndBudget = model([0, 0]);

  // Expenditure settings
  readonly showHourlyExpenditureRateFormField = model(false);
  readonly showBudgetHoursPerMemberFormField = model(false);

  // Data
  readonly multi = signal(false);

  // Form (uppercase values since they need to be added dynamically).
  public readonly formControlNames: FormControlNameRecord = {
    HourlyRate: 'HourlyRate',
    BudgetHoursPerMember: 'BudgetHoursPerMember',
    DeductFromBudget: 'DeductFromBudget',
  };
  public readonly faPenToSquare = faPenToSquare;

  // DI
  public readonly projectMemberGridService = inject(ProjectMemberGridService);
  private readonly shiveDialogService = inject(ShiveDialogService);

  // Misc
  private form: FormGroup = null;
  private readonly fb = inject(FormBuilder);

  // Apply result of updated history rate entries.
  public readonly makeApplyResult = (entries: ExpenditureRateEntry[]) => {
    this.memberGridRow.update((oldGridRow) => {
      this.rateAndBudget.update((oldRateAndBudget) => {
        const newRate = entries[entries.length - 1].rate;
        const oldBudget = oldRateAndBudget[1];
        return [newRate, oldBudget];
      });
      return { ...oldGridRow, expenditureRateHistory: entries };
    });
  };

  ngOnInit(): void {
    this.multi.set(this.userOperation() === UserOperation.Create);
  }

  // Add the controls to the child's form when the template first renders.
  public addExtraControls(form: FormGroup): boolean {
    if (this.showHourlyExpenditureRateFormField() && !form.get(this.formControlNames.HourlyRate)) {
      form.addControl(
        this.formControlNames.HourlyRate,
        this.fb.control<number | null>(null, [Validators.min(0)]),
      );
    }

    if (
      this.showBudgetHoursPerMemberFormField() &&
      !form.get(this.formControlNames.BudgetHoursPerMember)
    ) {
      const seconds = this.memberGridRow().budgetSeconds;
      const timeForPicker = DateTimeHelper.secondsToTimeOfDay(seconds);

      form.addControl(
        this.formControlNames.BudgetHoursPerMember,
        this.fb.control<Date | null>(timeForPicker),
      );
    }

    // Deduct from budget
    form.addControl(
      this.formControlNames.DeductFromBudget,
      this.fb.control<boolean | null>(this.memberGridRow().deductCostsFromBudget),
    );

    this.form = form;
    return true;
  }

  @Autobind
  public dialogClosed(selectedMembers: UserModel | UserModel[]): ProjectMemberGridRow[] {
    if (!selectedMembers || (Array.isArray(selectedMembers) && selectedMembers.length === 0)) {
      this.shiveDialogService.close(UserOperation.Cancel);
      return;
    }

    const time = this.form.get(this.formControlNames.BudgetHoursPerMember)?.value as Date | null;
    const budgetSeconds = time ? DateTimeHelper.timeOfDayToSeconds(time) : null;
    const deductCostsFromBudget = !!this.form.get(this.formControlNames.DeductFromBudget)?.value;

    if (Array.isArray(selectedMembers)) {
      return selectedMembers.map((member) => {
        return {
          ...this.memberGridRow(),
          member,
          budgetSeconds,
          deductCostsFromBudget,
        };
      });
    }

    this.memberGridRow.update((old) => {
      return {
        ...old,
        member: selectedMembers,
        budgetSeconds,
        deductCostsFromBudget,
      };
    });

    return [this.memberGridRow()];
  }
}
